import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@components/Atoms/Box';
import Typography from '@components/Atoms/Typography';
import { Chip } from '@mui/material';
import TreeViewSearch from '@components/Atoms/MultiSelectTree/TreeViewSearch';
import ShowIf from '@components/Atoms/ShowIf';
import Tooltip from '@components/Atoms/Tooltip';
import StarIcon from '@mui/icons-material/Star';
import { useConfigure, useRefinementList } from 'react-instantsearch';

const ADMIN_URL = process.env.REACT_APP_ADMIN_URL;

const NodeTree = ({
  assessmentNodes,
  selectedSkills,
  onQuestionClick,
  curriculumId,
  isUsingLegacyPractice,
}: {
  assessmentNodes: any[];
  selectedSkills: any[];
  onQuestionClick: (node: any) => void;
  curriculumId: number;
  isUsingLegacyPractice: boolean;
}) => {
  const [questionCountMap, setQuestionCountMap] = useState({});
  const [usedNodeMap, setUsedNodeMap] = useState({});

  useConfigure({
    filters: 'NOT Author:CT-User',
  });

  const { items: algoliaItems } = useRefinementList({
    attribute: 'Skill',
    limit: 10_000,
  });

  // 1. extract question count per node
  useEffect(() => {
    const questionCount = {};
    if (isUsingLegacyPractice) {
      assessmentNodes.forEach((node) => {
        questionCount[node.slug] = node.questions;
      });
    } else {
      algoliaItems.forEach((item) => {
        questionCount[item.value] = item.count;
      });
    }
    setQuestionCountMap(questionCount);
  }, [assessmentNodes, algoliaItems, isUsingLegacyPractice]);

  // 3. extract used skills
  useEffect(() => {
    const nodeMap = {};
    const usedSkillIds = selectedSkills.map((skill) => skill.id);
    assessmentNodes.forEach((node) => {
      if (usedSkillIds.includes(node.id)) {
        const pathParts = node.path.match(/.{1,4}/g);
        let cummulativeParts = '';
        pathParts.forEach((part) => {
          cummulativeParts += part;
          if (cummulativeParts.length > 4) {
            nodeMap[cummulativeParts] = true;
          }
        });
      }
    });
    setUsedNodeMap(nodeMap);
  }, [assessmentNodes, selectedSkills]);

  // build a tree of nodes
  const treeNodes = useMemo(() => {
    const nodeMap = {};
    const roots = [];

    if (!assessmentNodes || assessmentNodes.length === 0) {
      return [];
    }

    assessmentNodes.forEach((node) => {
      nodeMap[node.path] = { ...node, children: [] };
    });

    assessmentNodes.forEach((node) => {
      const parentPath = node.path.slice(0, -4);
      if (nodeMap[parentPath]) {
        nodeMap[parentPath].children.push(nodeMap[node.path]);
      } else {
        roots.push(nodeMap[node.path]);
      }
    });

    return roots;
  }, [assessmentNodes]);

  const updateTreeMetadata = useCallback(
    (tree: any[]) => {
      const generateNodeLabel = (node: any) => {
        node.id = node.id.toString();
        const questions = node.questions || questionCountMap[node.slug] || 0;
        const isUsed = node.path in usedNodeMap;

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              justifyContent: 'space-between',
              width: '75%',
              py: '0.1em',
            }}
          >
            <Typography variant="body2">
              {node.name}
              <ShowIf If={isUsed}>
                <Tooltip title="This node or its descendants are currently used in the selected unit/lesson.">
                  <StarIcon
                    sx={{
                      fontSize: 14,
                      color: 'secondary.dark',
                      ml: 1,
                      verticalAlign: 'middle',
                    }}
                  />
                </Tooltip>
              </ShowIf>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                color="secondary.dark"
                label={`Lessons: ${node?.lessons ?? 0}`}
                size="small"
                title={`${node?.lessons ?? 0} Lessons`}
                onClick={() => {
                  if (node?.lessons > 0) {
                    window.open(
                      `${ADMIN_URL}/admin/contentgroups/lessongroup/?curriculum_id=${curriculumId}&node_id=${node.id}`,
                      '_blank',
                    );
                  }
                }}
                sx={{
                  width: '150px',
                }}
              />
              <Chip
                color="success"
                label={`Questions: ${questions}`}
                size="small"
                title={`${questions} Questions`}
                onClick={() => questions && onQuestionClick(node)}
                sx={{
                  width: '150px',
                }}
              />
            </Box>
          </Box>
        );
      };

      tree.forEach((node) => {
        node.label = generateNodeLabel(node);
        if (node?.children && node?.children.length > 0) {
          updateTreeMetadata(node.children);
        }
      });

      return tree;
    },
    [curriculumId, onQuestionClick, questionCountMap, usedNodeMap],
  );

  const updatedTreeNodes = useMemo(
    () => updateTreeMetadata(treeNodes),
    [updateTreeMetadata, treeNodes],
  );

  return (
    <Box sx={{ height: 600, overflowY: 'auto' }}>
      <TreeViewSearch
        id="practice-node-treeview"
        tree={updatedTreeNodes}
        searchText=""
        selected={[]}
        setSelectedNodes={() => undefined}
        multiple={false}
        defaultExpanded={[updatedTreeNodes?.[0]?.id] ?? []}
      />
    </Box>
  );
};

export default NodeTree;
